import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { useCurrentUser } from "Auth/Hooks/useCurrentUser";
import { AccessRoleCode, PartnerStatus } from "Infrastructure/Api/Api";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  formatStreetAddress,
  translateCountryCode,
} from "Infrastructure/Utils/AddressUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useUpdatePartnerStatusMutation } from "Partners/Api/Mutations/useUpdatePartnerStatusMutation";
import { usePartnerDetailQuery } from "Partners/Api/Queries/usePartnerDetailQuery";
import { useSuperiorPartnersQuery } from "Partners/Api/Queries/useSuperiorPartnersQuery";
import { ProjectListSection } from "Projects/Components/ProjectListSection";
import { BackButton } from "Shared/Buttons/BackButton";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";
import { EditAvailableModulesModal } from "../Components/EditAvailableModulesModal";
import { EditPartnerModal } from "../Components/EditPartnerModal";
import { PartnerAvailableModules } from "../Components/PartnerAvailableModules";
import { PartnerClients } from "../Components/PartnerClients";
import { PartnerDocumentsSection } from "../Components/PartnerDocumentsSection";
import { PartnerInformation } from "../Components/PartnerInformation";

export const PartnerDetailPage = (): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();
  const {
    isLoading: isUserLoading,
    hasRole,
    isRootPartner,
    isSysAdmin,
    user,
  } = useCurrentUser();

  const { partnerId } = useParams<{ partnerId: string }>();

  useEffect(() => {
    if (!isUserLoading && hasRole(AccessRoleCode.Partner) && !isRootPartner) {
      navigate(AppRouting.Dashboard.path());
    }
  }, [isUserLoading, hasRole, isRootPartner, navigate]);

  const [isEditModulesModalOpen, setIsEditModulesModalOpen] = useState(false);
  const [isEditPartnerModalOpen, setIsEditPartnerModalOpen] = useState(false);

  const { data: partner, isLoading } = usePartnerDetailQuery(partnerId);
  // Only SysAdmin can call supervisor-search endpoint; partner role gets 403
  const { data: superiorPartnersData } = useSuperiorPartnersQuery(isSysAdmin);
  const updatePartnerStatusMutation = useUpdatePartnerStatusMutation();

  const superiorPartnerOptions = useMemo(() => {
    if (isSysAdmin) {
      return (superiorPartnersData?.items ?? [])
        .filter((p) => p.partnerID !== partnerId)
        .map((p) => ({
          value: p.partnerID,
          label: p.companyName,
        }));
    }
    // Partner role: lock to current user's partner
    if (user?.partnerID && user?.partnerName) {
      return [{ value: user.partnerID, label: user.partnerName }];
    }
    return [];
  }, [isSysAdmin, superiorPartnersData, partnerId, user]);

  const lockedParentPartnerID = !isSysAdmin
    ? (user?.partnerID ?? undefined)
    : undefined;

  const handleStatusChange = async () => {
    if (!partnerId || !partner) return;

    const newStatus =
      partner.status === PartnerStatus.Active
        ? PartnerStatus.Inactive
        : PartnerStatus.Active;

    try {
      await updatePartnerStatusMutation.mutateAsync({
        partnerID: partnerId,
        status: newStatus,
      });

      toast.success(
        newStatus === PartnerStatus.Active
          ? t(Resources.Partners.PartnerDetail.ActivateSuccess)
          : t(Resources.Partners.PartnerDetail.DeactivateSuccess),
      );
    } catch {
      toast.error(t(Resources.Partners.PartnerDetail.StatusChangeError));
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Partners)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!partner) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Partners)}>
        <Typography>{t(Resources.Errors.PartnerNotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToPartners)}
          defaultPath={AppRouting.Partners.path()}
        />
      }
      title={partner.companyName}
      action={
        isSysAdmin ? (
          <Button
            variant="contained"
            color={
              partner.status === PartnerStatus.Active ? "error" : "success"
            }
            onClick={handleStatusChange}
            disabled={updatePartnerStatusMutation.isPending}
          >
            {partner.status === PartnerStatus.Active
              ? t(Resources.Partners.PartnerDetail.Deactivate)
              : t(Resources.Partners.PartnerDetail.Activate)}
          </Button>
        ) : undefined
      }
    >
      <Grid container spacing={Spacing.M}>
        <Grid size={6}>
          <Stack spacing={Spacing.M}>
            <PartnerInformation
              companyNumber={partner.companyNumber}
              taxNumber={partner.taxNumber}
              email={partner.email ?? ""}
              phone={partner.phone ?? ""}
              address={formatStreetAddress(
                partner.address?.street,
                partner.address?.streetNumber,
                partner.address?.orientationNumber,
              )}
              city={partner.address?.municipality ?? undefined}
              postalCode={partner.address?.postalCode ?? undefined}
              state={translateCountryCode(partner.address?.countryCode, t)}
              parentPartnerName={partner.parentPartnerName}
              parentPartnerID={partner.parentPartnerID}
              onEdit={() => setIsEditPartnerModalOpen(true)}
            />
            <PartnerDocumentsSection partnerID={partnerId!} />
          </Stack>
        </Grid>
        <Grid size={6}>
          <Stack spacing={Spacing.M}>
            <ProjectListSection
              title={t(Resources.Partners.PartnerDetail.Projects)}
              emptyMessage={t(Resources.Partners.PartnerDetail.NoProjects)}
              projects={partner.projects}
            />
            <PartnerClients clients={partner.clients} />
            <PartnerAvailableModules
              modules={partner.enabledModules}
              onEdit={
                isSysAdmin ? () => setIsEditModulesModalOpen(true) : undefined
              }
            />
          </Stack>
        </Grid>
      </Grid>

      <EditPartnerModal
        isOpen={isEditPartnerModalOpen}
        onClose={() => setIsEditPartnerModalOpen(false)}
        partner={partner}
        partnerID={partnerId!}
        superiorPartnerOptions={superiorPartnerOptions}
        lockedParentPartnerID={lockedParentPartnerID}
      />
      <EditAvailableModulesModal
        isOpen={isEditModulesModalOpen}
        onClose={() => setIsEditModulesModalOpen(false)}
        partnerID={partnerId}
      />
    </AuthenticatedLayout>
  );
};
